/* eslint-disable no-nested-ternary */
import { MouseEventHandler, useCallback, useState } from 'react';
import { MdContactPhone } from 'react-icons/md';
import swal from 'sweetalert';
import auxiliarData from 'components/Table/mockdata/details.json';
import Detail from 'typings/Detail';
import { useLocation } from 'react-router-dom';
import { Line, Table, Tbody, Td, Th, Thead, Tr } from './styles';

const getLastItem = (thePath: string) =>
  thePath.substring(thePath.lastIndexOf('/') + 1);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const result = auxiliarData.filter(
  d => d.device === getLastItem(window.location.pathname),
);

type SortOrder = 'ascn' | 'desc';

function calculeDate(time: string | number | Date) {
  if (time === "never")
    return Infinity;

  const x = new Date();
  const y = new Date(time);

  const diff = y.getTime() - x.getTime();

  const days = Math.floor(diff / 86400000);

  return days;
}

function sortData({
  tableData,
  sortKey,
  reverse,
}: {
  tableData: Detail[];
  sortKey: keyof Detail;
  reverse: boolean;
}) {
  if (!sortKey) return tableData;

  const sortedData = tableData.sort((a, b) => {
    return a[sortKey] > b[sortKey] ? 1 : -1;
  });

  if (reverse) {
    return sortedData.reverse();
  }

  return sortedData;
}

function SortButton({
  sortOrder,
  columnKey,
  sortKey,
  onClick,
}: {
  sortOrder: SortOrder;
  columnKey: keyof Detail;
  sortKey: keyof Detail;
  onClick: MouseEventHandler<HTMLButtonElement>;
}) {
  return (
    // eslint-disable-next-line react/button-has-type
    <button
      onClick={onClick}
      className={`${sortKey === columnKey && sortOrder === 'desc'
        ? 'sort-button sort-reverse'
        : 'sort-button'
        }`}
    >
      ▲
    </button>
  );
}

function SortableTable({ data }: { data: Detail[] }) {
  const [sortKey, setSortKey] = useState<keyof Detail>('duration');
  const [sortOrder, setSortOrder] = useState<SortOrder>('ascn');
  const location = useLocation();

  const deviceId = location.pathname.split('/').slice(-1).pop();
  const deviceDetails = data.filter(d => d.device === deviceId);

  const headers: { key: keyof Detail; label: string }[] = [
    { key: 'type', label: 'Type' },
    { key: 'duration', label: 'Duration' },
    { key: 'creation', label: 'Creation' },
    { key: 'expireDate', label: 'Due Date' },
    { key: 'accountable', label: 'Accountable' },
    { key: 'contact', label: 'Contact' },
    { key: 'phone', label: 'Phone' },
  ];

  const sortedData = useCallback(
    () =>
      sortData({
        tableData: deviceDetails,
        sortKey,
        reverse: sortOrder === 'desc',
      }),
    [deviceDetails, sortKey, sortOrder],
  );

  function changeSort(key: keyof Detail) {
    setSortOrder(sortOrder === 'ascn' ? 'desc' : 'ascn');

    setSortKey(key);
  }

  return (
    <Table>
      <Thead>
        <Tr>
          {headers.map(row => {
            return (
              <Th key={row.key}>
                {row.label}{' '}
                <SortButton
                  columnKey={row.key}
                  onClick={() => changeSort(row.key)}
                  {...{
                    sortOrder,
                    sortKey,
                  }}
                />
              </Th>
            );
          })}
        </Tr>
      </Thead>

      <Line />

      <Tbody>
        {sortedData().map(person => {
          return (
            <Tr
              onClick={() => {
                navigator.clipboard.writeText(person.phone);
                swal(`Phone ${person.phone} copied to clipboard`);
              }}
            >
              <Td>{person.type}</Td>
              <Td>{person.duration}</Td>
              <Td>{person.creation}</Td>
              <Td
                backgroundColor={
                  calculeDate(person.expireDate) > 15
                    ? calculeDate(person.expireDate) >= 15 &&
                      calculeDate(person.expireDate) <= 30
                      ? 'var(--color-system-orange)'
                      : calculeDate(person.expireDate) > 30 &&
                        calculeDate(person.expireDate) < 90
                        ? 'var(--color-system-yellow)'
                        : 'var(--color-system-green)'
                    : 'var(--color-system-red)'
                }
              >
                {person.expireDate}
              </Td>
              <Td>{person.accountable}</Td>
              <Td>{person.contact}</Td>
              <Td>
                <MdContactPhone id="phone_icon" />
                {person.phone}
              </Td>
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
}

export default SortableTable;
